import * as Headless from '@headlessui/react';
import { CheckIcon, ChevronDownIcon } from '@heroicons/react/20/solid';
import { GlobeAltIcon } from '@heroicons/react/24/outline';
import { Switch } from '@oysterjs/ui/switch';
import clsx from 'clsx';
import React from 'react';
import type {
  AgencyUsagePeriod,
  AgencyUsageAllowances,
  AgencyUsageReport,
  UsageMeterDailyAmount
} from '~/api/graphql';
import { formatAgencyTimezoneLabel, usCanadaTimezoneOptionsForValue } from '~/lib/agency-timezones';
import { formatUsageUnits, parseUsageValue } from '~/lib/usage';
import { projectPeriodUsage, toDate } from './usage-utils';

type MeterKey = 'platformUnits' | 'kbPages' | 'voiceMinutes';

const METER_SEGMENTS: { key: MeterKey; label: string }[] = [
  { key: 'platformUnits', label: 'Platform units' },
  { key: 'kbPages', label: 'Knowledge base pages' },
  { key: 'voiceMinutes', label: 'Voice minutes' }
];

const compactNumberFormatter = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  maximumFractionDigits: 1
});

const TRIGGER_CLASS = clsx(
  'inline-flex items-center gap-2 rounded-full border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 px-3 py-1.5',
  'text-xs font-medium text-neutral-700 dark:text-neutral-300',
  'hover:border-neutral-300 dark:hover:border-white/20 hover:bg-neutral-50 dark:hover:bg-white/5'
);

const PANEL_CLASS = clsx(
  'z-20 mt-1 w-64 rounded-xl bg-white dark:bg-neutral-800 p-2 shadow-lg',
  'origin-top transition data-[closed]:scale-95 data-[closed]:opacity-0',
  'data-[enter]:duration-100 data-[enter]:ease-out data-[leave]:duration-75 data-[leave]:ease-in'
);

const shortDateFormatter = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  timeZone: 'UTC'
});

const MeterPicker = ({
  selected,
  onChange
}: {
  selected: MeterKey;
  onChange: (key: MeterKey) => void;
}) => {
  const selectedLabel =
    METER_SEGMENTS.find((segment) => segment.key === selected)?.label ?? 'Platform units';

  return (
    <Headless.Popover className="relative">
      {({ close }) => (
        <>
          <Headless.PopoverButton className={TRIGGER_CLASS} aria-label="Usage meter">
            <span>{selectedLabel}</span>
            <ChevronDownIcon className="size-3.5 text-neutral-400 dark:text-neutral-500" />
          </Headless.PopoverButton>
          <Headless.PopoverPanel transition anchor="bottom end" className={PANEL_CLASS}>
            <div className="px-2 py-1 text-[11px] font-medium uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
              Usage meter
            </div>
            <div className="flex flex-col">
              {METER_SEGMENTS.map((segment) => {
                const isSelected = segment.key === selected;
                return (
                  <button
                    key={segment.key}
                    type="button"
                    onClick={() => {
                      onChange(segment.key);
                      close();
                    }}
                    className={clsx(
                      'flex items-center justify-between gap-2 rounded-md px-2 py-1.5 text-sm',
                      isSelected
                        ? 'bg-primary-50 dark:bg-primary-500/10 font-medium text-primary-900 dark:text-primary-200'
                        : 'text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-white/5'
                    )}
                  >
                    <span>{segment.label}</span>
                    {isSelected && (
                      <CheckIcon className="size-4 text-primary-600 dark:text-primary-400" />
                    )}
                  </button>
                );
              })}
            </div>
          </Headless.PopoverPanel>
        </>
      )}
    </Headless.Popover>
  );
};

const TimezonePicker = ({
  timezone,
  onTimezoneChange
}: {
  timezone: string;
  onTimezoneChange: (timezone: string) => void;
}) => {
  const options = usCanadaTimezoneOptionsForValue(timezone);
  const label = formatAgencyTimezoneLabel(timezone);

  return (
    <Headless.Popover className="relative">
      {({ close }) => (
        <>
          <Headless.PopoverButton
            className={TRIGGER_CLASS}
            aria-label={`Timezone (currently ${label})`}
          >
            <GlobeAltIcon
              className="size-3.5 shrink-0 text-neutral-500 dark:text-neutral-400"
              aria-hidden
            />
            <span>{label}</span>
            <ChevronDownIcon className="size-3.5 text-neutral-400 dark:text-neutral-500" />
          </Headless.PopoverButton>
          <Headless.PopoverPanel transition anchor="bottom end" className={PANEL_CLASS}>
            <div className="px-2 py-1 text-[11px] font-medium uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
              Timezone
            </div>
            <div className="flex max-h-64 flex-col overflow-y-auto">
              {options.map((option) => {
                const isSelected = option.value === timezone;
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => {
                      onTimezoneChange(option.value);
                      close();
                    }}
                    className={clsx(
                      'flex items-center justify-between gap-2 rounded-md px-2 py-1.5 text-sm',
                      isSelected
                        ? 'bg-primary-50 dark:bg-primary-500/10 font-medium text-primary-900 dark:text-primary-200'
                        : 'text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-white/5'
                    )}
                  >
                    <span>{option.label}</span>
                    {isSelected && (
                      <CheckIcon className="size-4 text-primary-600 dark:text-primary-400" />
                    )}
                  </button>
                );
              })}
            </div>
          </Headless.PopoverPanel>
        </>
      )}
    </Headless.Popover>
  );
};

export type ChartPoint = {
  index: number;
  value: number;
  recorded: boolean;
  used: number;
  label: string;
  budgetValue?: number;
};

export type ChartGranularity = 'daily' | 'weekly';

export type ChartData = {
  points: ChartPoint[];
  periodBudget: number;
  maxValue: number;
  cumulative: boolean;
};

const getChartCopy = (granularity: ChartGranularity, cumulative: boolean) => {
  if (cumulative) {
    const bucket = granularity === 'weekly' ? 'week' : 'day';
    return {
      title: 'Cumulative consumption',
      budgetLabel: 'Expected pace',
      usageLabel: 'Cumulative',
      emptyState: 'No cumulative usage data yet.',
      unavailable: 'Cumulative usage data is not available for this usage period yet.',
      footer: `Bars show running totals through each ${bucket}. Solid bars include recorded usage; lighter bars add projected ${bucket}s at the current run rate. The dashed line is the expected pace to stay within plan.`
    };
  }

  const isWeekly = granularity === 'weekly';
  return {
    title: isWeekly ? 'Weekly consumption' : 'Daily consumption',
    budgetLabel: isWeekly ? 'Weekly budget' : 'Daily budget',
    usageLabel: null as string | null,
    emptyState: isWeekly ? 'No weekly usage data yet.' : 'No daily usage data yet.',
    unavailable: isWeekly
      ? 'Weekly usage data is not available for this usage period yet.'
      : 'Daily usage data is not available for this usage period yet.',
    footer: isWeekly
      ? 'Solid bars are recorded usage; lighter bars are projected at the current run rate. The dashed line is the weekly budget to stay within plan.'
      : 'Solid bars are recorded usage; lighter bars are projected at the current run rate. The dashed line is the daily budget to stay within plan.'
  };
};

export const buildWeeklyChartData = (
  daily: UsageMeterDailyAmount[] | null | undefined,
  daysElapsed: number,
  daysTotal: number,
  included: number,
  used: number
) => {
  const safeDaily = daily ?? [];
  const weeksTotal = Math.ceil(daysTotal / 7);
  const avgPerDay = daysElapsed > 0 ? used / daysElapsed : 0;
  const weeklyBudget = weeksTotal > 0 ? included / weeksTotal : 0;

  const points = Array.from({ length: weeksTotal }, (_, weekIndex) => {
    const startDay = weekIndex * 7;
    const endDay = Math.min(startDay + 7, safeDaily.length || daysTotal);

    let value = 0;
    let recordedUsed = 0;
    for (let dayIndex = startDay; dayIndex < endDay; dayIndex++) {
      if (dayIndex < daysElapsed) {
        const dayUsed = parseUsageValue(safeDaily[dayIndex]?.used ?? 0);
        value += dayUsed;
        recordedUsed += dayUsed;
      } else {
        value += avgPerDay; // projected days in this week
      }
    }

    const weekNotStarted = startDay >= daysElapsed;

    const startDate = toDate(safeDaily[startDay]?.date);
    const endDate = toDate(safeDaily[endDay - 1]?.date);
    const label =
      startDate && endDate
        ? `${shortDateFormatter.format(startDate)} – ${shortDateFormatter.format(endDate)}`
        : `Week ${weekIndex + 1}`;

    return {
      index: weekIndex,
      value,
      recorded: !weekNotStarted, // solid if week has started (includes partial current week)
      used: recordedUsed,
      label
    };
  });

  const peak = points.reduce((max, p) => Math.max(max, p.value), 0);
  const maxValue = Math.max(peak, weeklyBudget, 1) * 1.08;

  return { points, periodBudget: weeklyBudget, maxValue, cumulative: false };
};

/** Running totals per week with a ramping budget line for annual periods. */
export const buildCumulativeWeeklyChartData = (
  daily: UsageMeterDailyAmount[] | null | undefined,
  daysElapsed: number,
  daysTotal: number,
  included: number,
  used: number
): ChartData => {
  const weekly = buildWeeklyChartData(daily, daysElapsed, daysTotal, included, used);
  let runningValue = 0;
  let runningUsed = 0;

  const points = weekly.points.map((point, weekIndex) => {
    runningValue += point.value;
    runningUsed += point.used;
    const daysThroughWeek = Math.min((weekIndex + 1) * 7, daysTotal);
    const budgetValue = daysTotal > 0 ? included * (daysThroughWeek / daysTotal) : 0;

    return {
      ...point,
      value: runningValue,
      used: runningUsed,
      budgetValue
    };
  });

  const peak = points.reduce((max, point) => Math.max(max, point.value), 0);
  const peakBudget = points.reduce((max, point) => Math.max(max, point.budgetValue ?? 0), 0);
  const maxValue = Math.max(peak, peakBudget, included, 1) * 1.08;

  return { points, periodBudget: included, maxValue, cumulative: true };
};

/** Build recorded + projected points and daily-budget scale for the period chart. */
export const buildDailyChartData = (
  daily: UsageMeterDailyAmount[] | null | undefined,
  daysElapsed: number,
  daysTotal: number,
  included: number,
  used: number
): ChartData => {
  const safeDaily = daily ?? [];
  const barCount = safeDaily.length > 0 ? safeDaily.length : daysTotal > 0 ? daysTotal : 0;
  const avgPerDay = daysElapsed > 0 ? used / daysElapsed : 0;
  const dailyBudget = daysTotal > 0 ? included / daysTotal : 0;

  const points = Array.from({ length: barCount }, (_, index) => {
    const recorded = index < daysElapsed;
    const dayUsed = parseUsageValue(safeDaily[index]?.used ?? 0);
    const date = toDate(safeDaily[index]?.date);
    return {
      index,
      value: recorded ? dayUsed : avgPerDay,
      recorded,
      used: dayUsed,
      label: date ? shortDateFormatter.format(date) : `Day ${index + 1}`
    };
  });

  const peak = points.reduce((max, point) => Math.max(max, point.value), 0);
  const maxValue = Math.max(peak, dailyBudget, 1) * 1.08;

  return { points, periodBudget: dailyBudget, maxValue, cumulative: false };
};

/** Running totals per day with a ramping budget line for monthly periods. */
export const buildCumulativeDailyChartData = (
  daily: UsageMeterDailyAmount[] | null | undefined,
  daysElapsed: number,
  daysTotal: number,
  included: number,
  used: number
): ChartData => {
  const dailyChart = buildDailyChartData(daily, daysElapsed, daysTotal, included, used);
  let runningValue = 0;
  let runningUsed = 0;

  const points = dailyChart.points.map((point, dayIndex) => {
    runningValue += point.value;
    runningUsed += point.used;
    const daysThrough = dayIndex + 1;
    const budgetValue = daysTotal > 0 ? included * (daysThrough / daysTotal) : 0;

    return {
      ...point,
      value: runningValue,
      used: runningUsed,
      budgetValue
    };
  });

  const peak = points.reduce((max, point) => Math.max(max, point.value), 0);
  const peakBudget = points.reduce((max, point) => Math.max(max, point.budgetValue ?? 0), 0);
  const maxValue = Math.max(peak, peakBudget, included, 1) * 1.08;

  return { points, periodBudget: included, maxValue, cumulative: true };
};

const CHART_WIDTH = 800;
const CHART_HEIGHT = 240;
const PADDING_LEFT = 24;
const PADDING_TOP = 16;
const PADDING_BOTTOM = 28;

const UsageBarChart = ({
  points,
  periodBudget,
  maxValue,
  unitLabel,
  granularity,
  cumulative
}: {
  points: ChartPoint[];
  periodBudget: number;
  maxValue: number;
  unitLabel: string;
  granularity: ChartGranularity;
  cumulative: boolean;
}) => {
  const copy = getChartCopy(granularity, cumulative);
  const [hover, setHover] = React.useState<{
    index: number;
    x: number;
    y: number;
  } | null>(null);

  if (points.length === 0) {
    return (
      <p className="py-12 text-center text-sm text-neutral-500 dark:text-neutral-400">
        {copy.emptyState}
      </p>
    );
  }

  const innerWidth = CHART_WIDTH - PADDING_LEFT;
  const innerHeight = CHART_HEIGHT - PADDING_TOP - PADDING_BOTTOM;
  const barSlot = innerWidth / points.length;
  const barWidth = Math.max(2, barSlot * 0.72);

  const toX = (index: number) => PADDING_LEFT + index * barSlot + (barSlot - barWidth) / 2;
  const toY = (value: number) => PADDING_TOP + innerHeight - (value / maxValue) * innerHeight;

  const gridYs = [0.25, 0.5, 0.75, 1].map((ratio) => ({
    y: PADDING_TOP + innerHeight * (1 - ratio),
    label: Math.round(maxValue * ratio)
  }));

  const labelIndices =
    points.length <= 6
      ? points.map((_, i) => i)
      : [0, Math.floor(points.length / 2), points.length - 1];

  const budgetY = !cumulative && periodBudget > 0 ? toY(periodBudget) : null;
  const budgetLinePath =
    cumulative && points.some((point) => (point.budgetValue ?? 0) > 0)
      ? points
          .map((point, index) => {
            const budgetValue = point.budgetValue ?? 0;
            const x = toX(index) + barWidth / 2;
            const y = toY(budgetValue);
            return `${index === 0 ? 'M' : 'L'} ${x} ${y}`;
          })
          .join(' ')
      : null;
  const hovered = hover !== null ? points[hover.index] : null;
  const hoveredBudget = cumulative ? (hovered?.budgetValue ?? 0) : periodBudget;
  const hoveredUsageLabel =
    hovered == null
      ? null
      : (copy.usageLabel ??
        (!hovered.recorded
          ? 'Projected'
          : hovered.used < hovered.value
            ? 'Recorded + projected'
            : 'Recorded'));

  return (
    <div className="relative">
      <svg
        viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
        className="aspect-[10/3] w-full"
        onMouseLeave={() => setHover(null)}
        onMouseMove={(event) => {
          const rect = event.currentTarget.getBoundingClientRect();
          const relativeX = ((event.clientX - rect.left) / rect.width) * CHART_WIDTH - PADDING_LEFT;
          const idx = Math.max(0, Math.min(points.length - 1, Math.floor(relativeX / barSlot)));
          setHover({
            index: idx,
            x: event.clientX - rect.left,
            y: event.clientY - rect.top
          });
        }}
      >
        {gridYs.map((grid) => (
          <g key={grid.y}>
            <line
              x1={PADDING_LEFT}
              x2={CHART_WIDTH}
              y1={grid.y}
              y2={grid.y}
              className="stroke-neutral-200 dark:stroke-neutral-700"
              strokeDasharray="3 3"
            />
            <text
              x={0}
              y={grid.y + 3}
              textAnchor="start"
              fontSize="8"
              className="fill-neutral-400 dark:fill-neutral-500"
            >
              {compactNumberFormatter.format(grid.label)}
            </text>
          </g>
        ))}

        {budgetLinePath !== null && (
          <g pointerEvents="none">
            <path
              d={budgetLinePath}
              fill="none"
              className="stroke-neutral-400 dark:stroke-neutral-500"
              strokeWidth="1.5"
              strokeDasharray="5 3"
            />
            {(() => {
              const lastPoint = points[points.length - 1];
              const lastBudget = lastPoint?.budgetValue ?? 0;
              if (lastBudget <= 0) {
                return null;
              }
              return (
                <text
                  x={CHART_WIDTH}
                  y={toY(lastBudget) - 4}
                  textAnchor="end"
                  fontSize="9"
                  fontWeight="600"
                  className="fill-neutral-500 dark:fill-neutral-400 stroke-white dark:stroke-neutral-800"
                  strokeWidth="4"
                  paintOrder="stroke fill"
                >
                  {copy.budgetLabel}
                </text>
              );
            })()}
          </g>
        )}

        {budgetY !== null && (
          <g pointerEvents="none">
            <line
              x1={PADDING_LEFT}
              x2={CHART_WIDTH}
              y1={budgetY}
              y2={budgetY}
              className="stroke-neutral-400 dark:stroke-neutral-500"
              strokeWidth="1.5"
              strokeDasharray="5 3"
            />
            <text
              x={CHART_WIDTH}
              y={budgetY - 4}
              textAnchor="end"
              fontSize="9"
              fontWeight="600"
              className="fill-neutral-500 dark:fill-neutral-400 stroke-white dark:stroke-neutral-800"
              strokeWidth="4"
              paintOrder="stroke fill"
            >
              {copy.budgetLabel}
            </text>
          </g>
        )}

        {points.map((point) => {
          const height = Math.max(point.value <= 0 ? 2 : 4, (point.value / maxValue) * innerHeight);
          const y = PADDING_TOP + innerHeight - height;
          const isHovered = hover?.index === point.index;
          return (
            <rect
              key={point.index}
              x={toX(point.index)}
              y={y}
              width={barWidth}
              height={height}
              rx={2}
              fill={point.recorded ? '#0EA5E9' : '#0EA5E933'}
              fillOpacity={isHovered ? 1 : 0.9}
            />
          );
        })}

        {labelIndices.map((index) => (
          <text
            key={index}
            x={toX(index) + barWidth / 2}
            y={CHART_HEIGHT - 8}
            textAnchor="middle"
            fontSize="8"
            className="fill-neutral-400 dark:fill-neutral-500"
          >
            {granularity === 'weekly'
              ? (points[index].label.split(' – ')[0] ?? points[index].label)
              : points[index].label}
          </text>
        ))}

        {hover !== null && (
          <line
            x1={toX(hover.index) + barWidth / 2}
            x2={toX(hover.index) + barWidth / 2}
            y1={PADDING_TOP}
            y2={PADDING_TOP + innerHeight}
            className="stroke-neutral-900 dark:stroke-white"
            strokeOpacity={0.15}
            strokeDasharray="3 3"
            pointerEvents="none"
          />
        )}
      </svg>

      {hovered && hover && (
        <div
          className={clsx(
            'pointer-events-none absolute z-20 min-w-[10rem] -translate-x-1/2 rounded-lg bg-neutral-900/95 px-3 py-2 text-xs text-white shadow-lg ring-1 ring-white/10',
            hover.y < 72 ? 'translate-y-3' : '-translate-y-[calc(100%+10px)]'
          )}
          style={{
            left: `clamp(5rem, ${hover.x}px, calc(100% - 5rem))`,
            top: hover.y
          }}
        >
          <div className="font-semibold">{hovered.label}</div>
          <div className="mt-2 space-y-1">
            <div className="flex items-center justify-between gap-3">
              <span className="flex items-center gap-1.5 text-neutral-300 dark:text-neutral-600">
                <span
                  className="size-2 rounded-full"
                  style={{ backgroundColor: hovered.recorded ? '#0EA5E9' : '#7DD3FC' }}
                />
                {hoveredUsageLabel}
              </span>
              <span className="font-semibold tabular-nums">
                {formatUsageUnits(hovered.value)} {unitLabel}
              </span>
            </div>
            {hoveredBudget > 0 && (
              <div className="flex items-center justify-between gap-3">
                <span className="flex items-center gap-1.5 text-neutral-300 dark:text-neutral-600">
                  <svg width="10" height="4" className="shrink-0" aria-hidden>
                    <line
                      x1="0"
                      y1="2"
                      x2="10"
                      y2="2"
                      className="stroke-neutral-400 dark:stroke-neutral-500"
                      strokeWidth="1.5"
                      strokeDasharray="2 1.5"
                    />
                  </svg>
                  {copy.budgetLabel}
                </span>
                <span className="font-semibold tabular-nums">
                  {formatUsageUnits(hoveredBudget)} {unitLabel}
                </span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

type UsagePeriodChartProps = {
  report: Pick<AgencyUsageReport, 'platformUnits' | 'voiceMinutes' | 'kbPages'> | null | undefined;
  allowances: AgencyUsageAllowances | null | undefined;
  period: AgencyUsagePeriod | null | undefined;
  timezone: string;
  onTimezoneChange: (timezone: string) => void;
  chartGranularity: ChartGranularity;
};

export const UsagePeriodChart = ({
  report,
  allowances,
  period,
  chartGranularity,
  timezone,
  onTimezoneChange
}: UsagePeriodChartProps) => {
  const [selected, setSelected] = React.useState<MeterKey>('platformUnits');
  const [cumulative, setCumulative] = React.useState(false);
  const copy = getChartCopy(chartGranularity, cumulative);

  if (!report || !allowances || !period) {
    return (
      <div className="rounded-lg border border-neutral-200 bg-white px-6 py-16 text-center dark:border-neutral-700 dark:bg-neutral-800">
        <p className="text-sm text-neutral-500 dark:text-neutral-400">{copy.unavailable}</p>
      </div>
    );
  }

  const meter = report[selected];
  const allowance = allowances[selected];
  const daysElapsed = period.daysElapsed;
  const daysTotal = period.daysTotal;
  const used = parseUsageValue(meter.used);
  const included = allowance.included;
  const unitLabel = allowance.unitLabel;
  const projected = projectPeriodUsage(used, daysElapsed, daysTotal);
  const projectedOver = included > 0 && projected > included;
  const overBy = projectedOver ? projected - included : 0;

  const chartData =
    chartGranularity === 'daily'
      ? cumulative
        ? buildCumulativeDailyChartData(meter.daily, daysElapsed, daysTotal, included, used)
        : buildDailyChartData(meter.daily, daysElapsed, daysTotal, included, used)
      : cumulative
        ? buildCumulativeWeeklyChartData(meter.daily, daysElapsed, daysTotal, included, used)
        : buildWeeklyChartData(meter.daily, daysElapsed, daysTotal, included, used);
  const { points, periodBudget, maxValue } = chartData;

  return (
    <div className="divide-y divide-neutral-200 dark:divide-neutral-700 rounded-lg bg-white dark:bg-neutral-800 shadow">
      <div className="px-4 py-5 sm:px-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <h3 className="m-0 p-0 text-lg font-medium text-neutral-900 dark:text-white">
              {copy.title}
            </h3>
            <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
              {formatUsageUnits(used)} / {formatUsageUnits(included)} {unitLabel} used
              {' · '}
              Projected {formatUsageUnits(projected)}
              {projectedOver ? ` · ${formatUsageUnits(overBy)} over plan` : ' · within plan'}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className={TRIGGER_CLASS}>
              <span>Cumulative</span>
              <Switch
                color="sky"
                checked={cumulative}
                onChange={setCumulative}
                aria-label="Show cumulative usage"
              />
            </div>
            <TimezonePicker timezone={timezone} onTimezoneChange={onTimezoneChange} />
            <MeterPicker selected={selected} onChange={setSelected} />
          </div>
        </div>
      </div>

      <div className="px-4 py-5 sm:px-6">
        <UsageBarChart
          points={points}
          periodBudget={periodBudget}
          maxValue={maxValue}
          unitLabel={unitLabel}
          granularity={chartGranularity}
          cumulative={chartData.cumulative}
        />
        <p className="mt-3 text-xs text-neutral-500 dark:text-neutral-400">{copy.footer}</p>
      </div>
    </div>
  );
};
